import * as zod from 'zod/v4';

/** Represents an error thrown by the fetchJson utility when an HTTP request fails. */
class FetchError extends Error {
    /** Set it to "FetchError" to identify the error type */
    name = "FetchError";
    /** The HTTP status code returned by the failed request */
    status;
    /** The error body from the server, if available */
    errorBody;
    constructor({ status, errorBody }) {
        super(`Request failed with status ${status}`);
        this.status = status;
        this.errorBody = errorBody;
    }
}

/** A wrapper for fetch that expects a JSON and throws a typed error if fails */
async function fetchJson({ url, options, }) {
    const { method, headers = {}, body, credentials = "include" } = options;
    const finalHeaders = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "Content-Type": "application/json",
        ...headers,
    };
    const response = await fetch(url, {
        method,
        headers: finalHeaders,
        body: body ? JSON.stringify(body) : undefined,
        credentials,
    });
    if (!response.ok) {
        const contentType = response.headers.get("content-type") || "";
        let errorBody;
        try {
            errorBody = contentType.includes("application/json")
                ? await response.json()
                : await response.text();
        }
        catch {
            errorBody = null;
        }
        throw new FetchError({ status: response.status, errorBody });
    }
    return response.json();
}

/**
 * Reusable abstraction wrapping the logic to communicating with core api
 * Allows to make type-safe GET and POST requests
 */
class BaseApiClient {
    baseUrl;
    headers;
    onLogAssertion;
    urls = {
        usersV3: "/v3/users",
        dashboardV3: "/v3/dashboard",
        companyV3: "/v3/company",
        projectsV3: "/v3/projects",
        notificationV3: "/v3/notification",
    };
    constructor({ baseUrl, headers, onLogAssertion }) {
        this.baseUrl = baseUrl;
        this.headers = headers ?? {};
        this.onLogAssertion = onLogAssertion;
    }
    /** Sends a GET request and return the data property */
    async get(path) {
        const response = await fetchJson({
            url: `${this.baseUrl}${path}`,
            options: {
                method: "GET",
                headers: this.headers,
            },
        });
        return response.data;
    }
    /** Sends a POST request and return the data property */
    async post(path, body) {
        const response = await fetchJson({
            url: `${this.baseUrl}${path}`,
            options: {
                method: "POST",
                headers: this.headers,
                body,
            },
        });
        return response.data;
    }
    /** Sends a PUT request and return the data property */
    async put(path, body) {
        const response = await fetchJson({
            url: `${this.baseUrl}${path}`,
            options: {
                method: "PUT",
                headers: this.headers,
                body,
            },
        });
        return response.data;
    }
    /** Sends a DELETE request and return the data property */
    async delete(path) {
        const response = await fetchJson({
            url: `${this.baseUrl}${path}`,
            options: {
                method: "DELETE",
                headers: this.headers,
            },
        });
        return response.data;
    }
    /**
     * Validates an object against a Zod schema.
     * @param obj The object to validate against the schema.
     * @param schema The Zod schema to validate against.
     * @param routeName Optional route name for logging purposes.
     * @returns The same object, no modifications are made.
     */
    assertSchema(obj, schema, routeName) {
        const result = schema.safeParse(obj);
        if (!result.success && this.onLogAssertion) {
            const errorMessage = `Assertion failed for route "${routeName}": ${result.error.issues
                .map((e) => e.message)
                .join(", ")}`;
            this.onLogAssertion({
                message: errorMessage,
                error: result.error.issues,
                routeName,
            });
        }
        return result.success ? result.data : obj;
    }
}

/** Whether the user can create groups. Usually it is only possible to grant it to group managers. */
const createGroupPermissionSchema = zod.literal("createGrp");
/** Special permissions that can be granted to users. */
const userPermissionNameSchema = createGroupPermissionSchema;
/** Represents a single permission that can be granted to a user. */
const userPermissionSchema = zod.object({
    /** Name of the permission to either allow or deny. */
    name: userPermissionNameSchema,
    /**
     * Indicates whether the permission is allowed for the user.
     * If true, the user has the permission; if false, they do not.
     */
    allowed: zod.boolean(),
});
/** The permissions users can have on a project. Values match the Backend values */
const projectPermissionSchema = zod.enum([
    /**
     * This permission is granted to Enterprise Admins (of company) and Group Managers and project manager of project
     * They can archive, unarchive, download and delete the project
     */
    "*",
    /** Manage the users, add/remove members */
    "admin",
    /**
     * Only see the name, size, address etc. but you cannot open it
     * Use for people that are invited to snapshots
     */
    "describe",
    /** Open a project */
    "read",
    /** Open and change a project, e.g. upload slides */
    "write",
]);

/** Role of user in workspace */
const workspaceRoleSchema = zod.enum([
    "Administrator",
    "owner",
    "Enterprise Admin",
    "Enterprise Viewer",
    "Company Admin",
    "Group Manager",
    "Project Manager",
    "Company Member",
]);
const companyRoleSchema = zod.enum([
    /** Equal to enterprise admins. Have the highest role in company */
    "COMPANY_EXECUTIVE",
    /** Equal to Enterprise viewer */
    "COMPANY_VIEWER",
    /** Equal to group manager */
    "COMPANY_MANAGER",
    /**
     * A group member role with ability to view the group's details, setting, members and all projects,
     * including their contents and all projects of the group (including contents).
     */
    "GROUP_VIEWER",
    /** Project manager. In project view (EUserProjectRole) it is equal to project owner */
    "PROJECT_MANAGER",
    /**
     * Every role in company is also a member of it. This is the default role for every user in a company
     * If a new member is invited as a project viewer of a project, they become member of company
     * If that member is removed from project (as viewer), they still remain as member of company
     */
    "MEMBER",
]);
/** Role of user in a project */
const projectRoleSchema = zod.enum([
    "RESOURCE_OWNER",
    "RESOURCE_ADMIN",
    "RESOURCE_EDITOR",
    "RESOURCE_VIEWER",
]);

// TODO: This will replace to string only after all routes are updated to use companyId from CoreApiClient
/** Company ID */
const companyIdSchema = zod.union([zod.string(), zod.number()]);
// TODO: This will replace to string only after all routes are updated to use groupId from CoreApiClient
/** Project ID */
const projectIdSchema = zod.union([zod.string(), zod.number()]);
// TODO: This will replace to string only after all routes are updated to use groupId from CoreApiClient
/** Group ID */
const groupIdSchema = zod.union([zod.string(), zod.number()]);
/** User ID */
const userIdSchema = zod.string();
/** The unique identifier for the constraint of a plan */
const constraintIdSchema = zod.string();
/** A time stamp used through different time related properties */
const unixTimestampSchema = zod.number();
/** ISO 8601 representation of a timestamp */
const iso8601DateSchema = zod.string();
/** Different main subjects for adding members to them */
const memberSubjectTypeSchema = zod.enum([
    "company",
    "project",
    "group",
]);

/** Kind of users whether a user or a team */
const kindSchema = zod.enum(["user", "user-group"]);
/** Data that is returned when fetching for the user's hash in Intercom */
const intercomDataSchema = zod.object({
    /** The Id of the logged in user */
    userId: userIdSchema,
    /** Hash computed by the backend based on the userId and the secret key */
    hash: zod.string(),
});

/** User entity, contains all the information about the user */
const userSchema = zod.object({
    /**
     * The id of the user
     * Would be the same as identity if user is registered
     */
    id: userIdSchema.optional(),
    /**
     * The email of the user
     * Would be the same as identity if user is not registered
     */
    email: zod.string(),
    /**
     * equal to email if user is invited to company, but not registered
     * equal to id if user is registered
     */
    identity: userIdSchema,
    /** Name might be returned instead of firstName + lastName */
    name: zod.string().optional(),
    /** If the name of the user is not combined in name, it is split between first and last name */
    firstName: zod.string().optional(),
    /** If the name of the user is not combined in name, it is split between first and last name */
    lastName: zod.string().optional(),
    /** URL to the user's thumbnail image, can be an empty string if not available */
    thumbnailUrl: zod.string().optional(),
    /**
     * Specifies the type of the entity: either an individual user or a team.
     *
     * - `"user"`: Represents an individual user.
     * - `"user-group"`: Represents a team.
     */
    kind: kindSchema.optional(),
    /**
     * Array filled with permissions that the user has.
     * Each permission is represented by a name and a boolean indicating if it is allowed.
     */
    permissions: zod.array(userPermissionSchema),
});
/** User schema if the user is returned as a manager in company or project level */
const userAsManagerSchema = userSchema.omit({ permissions: true });
/** Base company member entity */
const companyMemberBaseSchema = userAsManagerSchema.extend({
    /**
     * The effective role of the member, including transitive assignments via user groups, meaning that
     * the highest role of the user in company or user groups will be returned in here
     */
    role: companyRoleSchema,
    /** The permissions that the member has. If a company permission is missing, its same as if its disallowed */
    permissions: zod.array(userPermissionSchema).optional(),
});
/** Response received from server when requesting members of a company */
const companyMemberDetailsSchema = companyMemberBaseSchema.extend({
    /** The role directly assigned to the user in the company */
    userRole: companyRoleSchema,
});
/**
 * User interface if the user is returned as a member in company or project level
 * The only different with manager is that they have a role prop
 */
const projectMemberBaseSchema = userAsManagerSchema.extend({
    /** The role of member in project */
    role: projectRoleSchema,
});
/** Base information of a member in a group */
const groupMemberSchema = companyMemberBaseSchema.extend({
    /** The date in which the user joined the group */
    dateJoined: unixTimestampSchema,
});

/** Details of a company */
const companyDetailsSchema = zod.object({
    /** ID of the company */
    id: companyIdSchema,
    /** Name of the company */
    name: zod.string(),
    /** This is for companies that have a Holobuilder subdomain and otherwise will be undefined */
    domain: zod.string().optional(),
    /** The optional company logo set by the user */
    logoUrl: zod.string().optional(),
    /** Array filled with tags relevant for a company. */
    tags: zod.array(zod.string()),
});
/** Details of a company with the role of the user in the company requesting it */
const companyWithRoleDetailsSchema = companyDetailsSchema.extend({
    /** Highest role of the current user in the company */
    role: companyRoleSchema,
});
/** Context of a company */
const companyContextSchema = zod.object({
    /** ID of the company */
    id: companyIdSchema,
    /** Name of the company */
    name: zod.string(),
    /** Current user's role in the company */
    role: companyRoleSchema.optional(),
});

/** All available workspace types */
const workspaceTypeSchema = zod.enum([
    /** A workspace connected to a dashboard */
    "company",
    /** Workspaces only accessible to HoloBuilder admins. Providing access to admin services (e.g. the admin panel) */
    "admin",
]);
/** A workspace containing the api, icon etc. */
const workspaceSchema = zod.object({
    /** The apiUrl of the workspace, i.e. where to send (login) requests */
    apiUrl: zod.string(),
    /** This is for companies that have a Holobuilder subdomain and otherwise will be undefined */
    domain: zod.string().optional(),
    /** Icon to show with the workspace, e.g. Company Logo for Dashboards. Can be an empty string if not available */
    icon: zod.string().optional(),
    /** Name of the workspace */
    name: zod.string(),
    /** The highest role the user has in the workspace */
    role: workspaceRoleSchema,
    /** The type of the workspace, can be either a normal (company workspace) or the admin panel */
    type: workspaceTypeSchema,
    /** Url to workspace, e.g. dashboard.xxx/workspace-id */
    url: zod.string(),
});

/**
 * Representing feature identifier. Backend is defined it as string, but we use strict type
 * making sure we are typing the feature names correctly
 */
const featureIdentifierSchemas = zod.enum([
    // These values are seen returned in companyContext
    "global-img360-write",
    "global-img360-read",
    "focus-processing",
    "user_role_fm",
    /** Feature Roles */
    "ft_pdf_export",
    "ft_image_2d",
    "ft_project_evaluation",
    "ft_download_360",
    /** Improved version of face blurring. Will replace v1 entirely soon. */
    "global-face-blurring",
    "ft_speed_mode",
    /** Enables the use of geo-referenced sheets */
    "webeditor-sheet-gps",
    /** Enables GPS-based capturing functionality in the JWA */
    "jwa_capture_gps",
    /** Project actions the current user is allowed to perform */
    "project-archive",
    "project-unarchive",
    "project-download",
    "project-create",
    /** Enabling the "Premium Analytics" (powered by SiteAI and Power BI) */
    "company-insights",
    /** Dashboard features returned in a company-context allowing a user to access specific parts of the Dashboard. */
    "dashboard-projects",
    "dashboard-insights",
    "dashboard-settings",
    "dashboard-templates",
    "dashboard-integrations",
    "dashboard-groups",
    "dashboard-user-directory",
    /**
     * Dashboard features returned in a project-context
     * Allows a user to access the "Integrations" tab in the project overview   *
     */
    "dashboard-project-integrations",
    /** Enables redirect to the Sphere Dashboard app (instead of the HoloBuilder Dashboard app) */
    "sphere-dashboard",
    /** Enables the creation of Snapshots from other Projects */
    "global-snapshot-create",
    /** Enables opening of Snapshots. */
    "global-snapshot-view",
    /** Makes the Video-Mode feature available in the AdminPanel */
    "global-videomode",
    /** Allows a user to open projects in sphere */
    "global-open-in-sphere-viewer",
    /** Allows the viewing of Point Clouds, e.g., in the Sphere Viewer. Doesn’t allow creating/updating Point Clouds */
    "global-pointcloud-read",
    /** Allows the creation, editing and removal of Point Clouds, e.g., in the Sphere Viewer */
    "global-pointcloud-write",
    /** Enables all workflows related to capturing with Orbis */
    "global-orbis-processing",
    /** Enables the import of CAD models by privileged users, e.g., in the Sphere Viewer. */
    "cad-import",
    /** Enables the possibility to display CAD models by privileged users, e.g., in the Sphere Viewer. */
    "cad-display",
    /** Standard sphere XG package */
    "sphere-xg-standard",
    /** Pro sphere XG package */
    "sphere-xg-pro",
    /** Enterprise sphere XG package */
    "sphere-xg-enterprise",
    /** Sphere XG 360 addon */
    "sphere-xg-addon-360",
    /** Sphere XG point cloud addon */
    "sphere-xg-addon-point-cloud",
    /** Allows Sphere Dashboard users to open projects in the HoloBuilder Web Editor */
    "open-in-webeditor",
    /** Enables dense point cloud generation for video mode jobs */
    "videomode-pointclouds",
    "user_role_fm_trial",
    /**
     * @deprecated
     */
    "ft_material_ai",
    /**
     * Disables/hides the material-ai UI (otherwise, no function)
     * @deprecated
     */
    "material_ai_processing_only",
    "ft_face_blurring",
    "ft_spotwalk",
    /**
     * Enabling a company to see any Power BI related UI in the Dashboard (i.e. companyInsights & projectProgress)
     * Note: The company still needs those flags to use Power BI, otherwise they have to request the feature
     */
    "insights-teaser",
    /** Enabling the "Project Progress" (powered by SiteAI and Power BI) */
    "project-progress",
    /**
     * Makes the Visual AI Search feature available.
     * @deprecated
     */
    "global-vais",
    /**
     * Makes the progress-ai feature available in the AdminPanel
     * Allows a user to access the "progress-ai" tab in the project overview
     */
    "global-progress-ai",
    /** Makes the point clouds SendTo feature available in the AdminPanel */
    "global-pointcloud-sendto",
    /** Service provider sphere XG package */
    "sphere-xg-service-provider",
    /** Allows a user to access the "Photogrammetry" feature in the Staging area of the Dashboard */
    "photogrammetry",
    /** Enables access to the tokens and floor-plan generator features. */
    "floorplan-generator",
]);
/** A string representing bundle identifier */
const bundleIdentifierSchemas = zod.enum([
    /** Includes Project creation, editing and download and basic Project-related features */
    "plan-fm",
    /** Contains the Value Pricing Model features and it is the same as plan-fm, except that it shows limits */
    "vpm",
    /** Includes the same features from the vpm bundler plus: gps, snapshot, premium analytics and videomode features */
    "vpm-enterprise",
    "SiteAI",
    "PDF Report",
    "Project Evaluations",
    "Download 360",
    "Face Blurring",
    /** Bundle to enable advanced GPS functionality in Web Editor and JobWalk App */
    "gps",
    /** Unpaid plan. Allows general Project usage. Does not include project download */
    "trial",
    /** Bundle assigned to Demo Projects */
    "demo-project",
    /** Educational users and Dashboards. Common feature set, but no Project download */
    "edu",
    /** All Dashboard Features */
    "dashboard",
    /** Bundle that contains the minimal set of Features that is assigned to every Dashboard indefinitely */
    "minimal",
    /**
     * Bundle containing the Premium Analytics feature, as well as the Insights Teaser feature
     * Putting the two features in a single bundle makes sure that we do not forget to provide the users with the teaser
     * feature when we enable Premium Analytics for them
     */
    "premium-insights",
    /** Bundle containing the necessary features for using the Project SnapShot feature */
    "snapshot",
    /** Includes all Point Cloud features, i.e. global-pointcloud-read, global-pointcloud-write  */
    "pointcloud",
    /** Includes all necessary features to enable all workflows related to cloud processing */
    "cloud-processing",
    /** Includes all necessary features to enable all workflows related to cloud processing as a trial */
    "cloud-processing-trial",
    /** Enables importing and editing of 3D Models */
    "cad",
    /** Grants access to Flash functionality of the Focus Laser Scanner for Stream App users. */
    "flash",
    /** Includes all features granting access to the Stream App. */
    "faro-stream",
    /** Standard sphere XG package */
    "sphere-xg-standard",
    /** Pro sphere XG package */
    "sphere-xg-pro",
    /** Enterprise sphere XG package */
    "sphere-xg-enterprise",
    /** Service provider sphere XG package */
    "sphere-xg-service-provider",
    /** Sphere XG 360 addon */
    "sphere-xg-addon-360",
    /** Sphere XG point cloud addon */
    "sphere-xg-addon-point-cloud",
    /**
     * Trial indicator for Sphere XG. Adding this to any other bundle will make it a trial.
     * This means that the backend will send banner notification when the time comes.
     * This is the only change made by backend. Other changes like 21 days duration should be set in the frontend
     */
    "sphere-xg-trial-indicator",
    /** Provides features required for Floorplan Generation. */
    "floorplan-generator",
]);
/** Indicates a feature based on existing plans with flags showing whether this is enabled and permitted or not */
const featureSchema = zod.object({
    /** The feature itself */
    identifier: featureIdentifierSchemas,
    /** A feature can be available but deactivated for the given context */
    enabled: zod.boolean(),
    /** True if the feature is allowed to be used by the logged in user */
    permitted: zod.boolean(),
    /** Date of expiration of the feature. Available for features of type subscription */
    expiresAt: iso8601DateSchema.optional(),
});

/** All the possible constraint types. Values are capitalized to match BE values */
const constraintTypeSchema = zod.enum([
    /** Plan is constrained by how big the projects are (area of a project is set by users during project creation) */
    "PROJECT_AREA",
    /** Plan is constrained by how many projects can be created */
    "PROJECT_COUNT",
    /** Plan is constrained by how many users can be added to a company (only makes sense for company plans) */
    "USER_COUNT",
    /** Plan is constrained by total number of waypoints */
    "WAYPOINT",
    /** Plan is constrained by how many timetravel can be assigned to a waypoint */
    "TIMETRAVEL_ON_WAYPOINT",
    /** Plan is constrained by the usage of storage size */
    "STORAGE",
    /** Plan is constrained by the total number of unique users having one of the roles */
    "USER_ROLE",
    /** Plan is constraint by credits */
    "CREDITS",
]);
/** Constraint limiting a plan by the provided type as returned by /adm routes */
const ConstraintSchema = zod.object({
    /** Unique identifier of the constraint */
    id: zod.string(),
    /** Discriminator to differentiate constraint-types */
    type: constraintTypeSchema,
    /** data assigned to the constraint type */
    data: zod.object({
        /** The actual limit assigned to this constraint */
        max: zod.number(),
        /**
         * The current number of constraint used so far for this constraint.
         * Changed this field to optional because the backend does not support it during the plan creation.
         * Although this field is required on the frontend, it must be excluded from the constraints
         * before sending the request to the backend to create a new plan.
         */
        current: zod.number().optional(),
        // TODO: Check whether this is needed or not when fetching constraints used in Sphere Dashboard
        /** The roles that can be assigned to this constraint */
        //   roles?: IConstraintUserRoles;
    }),
    /** Unix timestamp in ms when the constraint was created */
    createdAt: unixTimestampSchema,
    /** Unix timestamp in ms when the constraint was modified */
    modifiedAt: unixTimestampSchema,
});
/** Payload for creating constraint by the provided type */
const createConstraintPayloadSchema = ConstraintSchema.pick({
    type: true,
    data: true,
});
/** Payload for updating a constraint */
const updateConstraintPayloadSchema = createConstraintPayloadSchema.extend({
    /** Unique identifier of the constraint */
    id: constraintIdSchema,
});
/**
 * The possible values for the identifier of IProjectQuota
 * These identifiers indicate what the quota/limit is based on and what should be tracked and limited in usage
 */
const quotaIdentifierSchema = zod.enum([
    /** Identifier for maximum number of waypoint quota */
    "waypoint",
    /** Identifier for maximum number of timetravels per waypoint quota */
    "timetravel_on_waypoint",
    /** Identifier for maximum project area quota */
    "project_area",
    /** Identifier for maximum project count quota */
    "project_count",
    /** Identifier for maximum user count quota */
    "user_count",
    /** Identifier for maximum user role quota */
    "user_role",
    /** Identifier for maximum storage size quota */
    "storage",
    /** Identifier for maximum credits quota */
    "credits",
]);
/** Information about the VPM project limits */
const quotaSchema = zod.object({
    /** identifier specifying which type of VPM limit this is */
    identifier: quotaIdentifierSchema,
    /** The current/actual value, saying how much of the resources of the limit-type is used */
    current: zod.number(),
    /** The limit (max) value, how much of the resources of the limit-type can be used */
    limit: zod.number(),
    /** ISO-8601 date when the quota was reached/exceeded; only present if exceeded */
    exceededAt: iso8601DateSchema.optional(),
    /** ISO-8601 date on which this quota will cause a shutdown; only present if exceeded */
    deadline: iso8601DateSchema.optional(),
    /**
     * Whether or not the values (numbers/dates) can be relied on.
     * Reliability is affected, e.g., by processing delays due to errors in the back end.
     * The value should be 'true' most of the time. If the value is 'false', clients are
     * communicate this to the user, or to ignore a quota temporarily.
     */
    tentative: zod.boolean(),
});

/** Branding settings for company */
const brandingSettingsSchema = zod.object({
    logo: zod.object({
        url: zod.string(),
        target: zod.string(),
        showHoloBuilderLogo: zod.boolean(),
    }),
});
/** The Stripe product interface that is used in self checkout */
const stripeProductSchema = zod.object({
    /** The Stripe product ID */
    id: zod.string(),
    /** The name of the product */
    name: zod.string(),
    /** The description of the product */
    description: zod.string().optional(),
    /** Price information of the product */
    price: zod.object({
        /** The ID of the price in Stripe */
        priceId: zod.string(),
        /** The price amount in cents */
        price: zod.string(),
        /** Currency of the price */
        currency: zod.string(),
    }),
    /** Token amount included in the product */
    tokenAmount: zod.number(),
    /** The badge to show in the UI */
    badge: zod.string(),
});
/** Company communication settings */
const companyCommunicationSettingsSchema = zod.object({
    /** Language to be used for all communications (emails) */
    language: zod.string().optional(),
    /** Whether Sphere Viewer is the default application for opening a project */
    sphereViewerIsDefault: zod.boolean().optional(),
});
/** Allowed values for a subscription status. */
const subscriptionStatusSchema = zod.enum([
    "ACTIVE",
    "INVALID",
    "EXPIRED",
    "UPCOMING",
]);
/** Options for filtering company subscriptions */
const companySubscriptionOptionsSchema = zod.object({
    /** Subscription status to filter by (single or multiple values) */
    status: zod
        .union([subscriptionStatusSchema, subscriptionStatusSchema.array()])
        .optional(),
    /** Start date (ISO 8601 format) to filter subscriptions from */
    from: iso8601DateSchema.optional(),
    /** End date (ISO 8601 format) to filter subscriptions until */
    until: iso8601DateSchema.optional(),
});

/** Defines the schema for the getCurrentUsersWorkspaces response */
const getCurrentUsersWorkspacesSchema = zod.object({
    /** List of workspaces */
    workspaces: zod.array(workspaceSchema),
});
/** Defines the schema for type void */
const voidSchema = zod.undefined();
/** Defines the schema for an empty object */
const emptyObjectSchema = zod.object({}).strict();
/** Returns the user with updated permission */
const setMemberPermissionsInCompanySchema = userSchema
    .pick({
    identity: true,
    permissions: true,
})
    .required();
/** Payment intent information returned by the backend to confirm stripe payment */
const createStripePaymentIntentSchema = zod.object({
    /** The ID of the payment intent */
    paymentIntentId: zod.string(),
    /** The client secret of the payment intent */
    clientSecret: zod.string(),
});
/** Defines the schema for the getProjectContext response */
const getProjectContextSchema = zod.object({
    /** The company the project belongs to. Undefined for user-projects */
    company: companyContextSchema,
    /** All the features that are available based on existing plans (whether enabled or not). */
    features: zod.array(featureIdentifierSchemas),
    /**
     * List of available features to the given context
     * (including features that are not enabled or allowed to be used by the user)
     */
    featuresAvailable: zod.array(featureSchema),
    /** Permissions of this user on the project */
    permissions: zod.array(projectPermissionSchema).optional(),
    /** The id of the project the context-information applies to */
    projectId: projectIdSchema,
    /** information about the VPM project limits */
    quotas: zod.array(quotaSchema),
    /** Current user's role in the project */
    role: projectRoleSchema.optional(),
    /**
     * Array of roles granted by the assigned plan from the AdminPanel
     * @deprecated Use featuresAvailable instead
     */
    roles: zod.array(featureIdentifierSchemas),
});
/** Defines the schema for the getCompanyContext response */
const getCompanyContextSchema = zod.object({
    /**
     * All the features that are available based on existing plans (whether enabled or not)
     * @deprecated Use featuresAvailable instead
     */
    features: zod.array(featureIdentifierSchemas),
    /** Features that are available for this company */
    featuresAvailable: zod.array(featureSchema),
    /**
     * User roles granted by company subscription
     * @deprecated Use featuresAvailable instead
     */
    userRoles: zod.array(featureIdentifierSchemas),
    /** information about the VPM project limits */
    quotas: zod.array(quotaSchema),
    /** Information about a companies trial (available if applicable) */
    trial: zod
        .object({
        /**
         * Expiration date of the trial period.
         * Technically the last endDate of any Trial Plan (including inactive Plans) is returned here.
         */
        expires: iso8601DateSchema,
    })
        .optional(),
});
/** Defines the schema for the getCompanySubscriptions response */
const getCompanySubscriptionsSchema = zod.object({
    /** The label of a subscription */
    label: zod.string(),
    /** The status of a subscription */
    status: subscriptionStatusSchema,
    /** Features that are available for the subscription */
    features: zod.array(zod.object({
        /** The name of the feature */
        name: zod.string(),
        /** The unique identifier of the feature */
        identifier: featureIdentifierSchemas,
    })),
    /** List of constraints that are available for the subscription */
    constraints: zod.array(createConstraintPayloadSchema),
    /** The start date of a subscription */
    startDate: iso8601DateSchema,
    /** The expiration date of a subscription */
    endDate: iso8601DateSchema,
    /** Subject of the subscription, i.e. what/who the subscription grants features for. */
    subject: companyDetailsSchema.extend({
        createdAt: unixTimestampSchema,
    }),
});

/** Data generation used for company statistics */
const chartDataGenerationSchema = zod.enum([
    "cumulative",
    "differential",
]);
/** Type of server chart used for company statistics */
const serverChartTypeSchema = zod.enum(["BAR_CHART", "PIE_CHART"]);
/** Type of internal chart used for company statistics */
const internalChartTypeSchema = zod.enum(["bar", "pie", "line"]);
/** Name of statistics that can be requested */
const statisticNameSchema = zod.enum([
    "project-count",
    "project-count-abs",
    "project-sqft",
    "project-sqft-abs",
    "project-spheres",
    "project-spheres-abs",
    "project-sheets",
    "project-sheets-abs",
]);
/** Schema for axe property */
const axeSchema = zod.object({
    label: zod.string(),
    name: zod.string(),
});
/** Describes a company statistic chart */
const companyStatisticSchema = zod.object({
    axes: zod.object({
        x: axeSchema,
        y: axeSchema,
        z: axeSchema.optional(),
    }),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    "data-generation": chartDataGenerationSchema,
    name: statisticNameSchema,
    type: zod.union([serverChartTypeSchema, internalChartTypeSchema]),
    data: zod.array(zod.object({
        label: zod.string(),
        values: zod.array(zod.number()),
        year: zod.number(),
        month: zod.number(),
        day: zod.number(),
        hour: zod.number(),
        minute: zod.number(),
        second: zod.number(),
        timezone: zod.string(),
    })),
    title: zod.string(),
});

/**
 * Generic function to build query parameters from an object
 * e.g. { status: ["ACTIVE", "EXPIRED", "INVALID"] } becomes "?status=ACTIVE%2CEXPIRED%2CINVALID"
 */
function buildQueryParams(params) {
    const urlParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        // Skip undefined or null values
        if (!value) {
            continue;
        }
        const queryValue = Array.isArray(value) ? value.join(",") : String(value);
        if (queryValue === "") {
            // Skip empty strings
            continue;
        }
        urlParams.set(key, queryValue);
    }
    return urlParams.toString() ? `?${urlParams.toString()}` : "";
}

/** All the APIs for fetching and updating companies */
class CompanyClient extends BaseApiClient {
    /** Get the companies of the user */
    async getCompanies() {
        const response = await this.get(`${this.urls.dashboardV3}/company`);
        return this.assertSchema(response, zod.array(companyWithRoleDetailsSchema), "getCompanies");
    }
    /** Remove a member from a company */
    async removeMemberFromCompany({ companyId, userIdentity, }) {
        const response = await this.delete(`${this.urls.dashboardV3}/company/${companyId}/member/${encodeURIComponent(userIdentity)}`);
        return this.assertSchema(response, userAsManagerSchema, "removeMemberFromCompany");
    }
    /** Sets the company permissions for a member */
    async setMemberPermissionsInCompany({ companyId, userIdentity, payload, }) {
        const response = await this.post(`${this.urls.dashboardV3}/company/${companyId}/member/${encodeURIComponent(userIdentity)}/permission`, payload);
        return this.assertSchema(response, setMemberPermissionsInCompanySchema, "setMemberPermissionsInCompany");
    }
    /** Returns the branding settings for a company */
    async getBrandingSettings({ companyId, }) {
        const response = await this.get(`${this.urls.dashboardV3}/company/${companyId}/settings/branding`);
        return this.assertSchema(response, brandingSettingsSchema, "getBrandingSettings");
    }
    /** Updates the branding settings for a company */
    async updateBrandingSettings({ companyId, payload, }) {
        const response = await this.post(`${this.urls.dashboardV3}/company/${companyId}/settings/branding`, payload);
        return this.assertSchema(response, voidSchema, "updateBrandingSettings");
    }
    /**
     * Returns the communication settings for a company needed for later use
     * This means the language the user uses the app and whether the user is using sphere viewer by default
     */
    async getCompanyCommunicationSettings({ companyId, }) {
        const response = await this.get(`${this.urls.dashboardV3}/company/${companyId}/settings/communication`);
        return this.assertSchema(response, companyCommunicationSettingsSchema, "getCompanyCommunicationSettings");
    }
    /** Get all the company statistics for the last 3 years */
    async getCompanyStatistics({ companyId, }) {
        const response = await this.get(`${this.urls.dashboardV3}/company/${companyId}/stats`);
        return this.assertSchema(response, zod.array(companyStatisticSchema), "getCompanyStatistics");
    }
    /** Updates the communication settings for a company */
    async updateCompanyCommunicationSettings({ companyId, payload, }) {
        const response = await this.post(`${this.urls.dashboardV3}/company/${companyId}/settings/communication`, payload);
        return this.assertSchema(response, voidSchema, "updateCompanyCommunicationSettings");
    }
    /** Get the Stripe products for the company */
    async getStripeProducts({ companyId, }) {
        const response = await this.get(`/stripe/product/${companyId}`);
        return this.assertSchema(response, zod.array(stripeProductSchema), "getStripeProducts");
    }
    // TODO: This is not tested yet
    /** Create a Stripe payment intent for purchasing a product */
    // async createStripePaymentIntent(
    //   payload: CreateStripePaymentIntentPayload,
    // ): Promise<CreateStripePaymentIntent> {
    //   const response: CreateStripePaymentIntent = await this.post(
    //     "/stripe/product/purchase",
    //     payload,
    //   );
    //   return this.assertSchema(
    //     response,
    //     createStripePaymentIntentSchema,
    //     "createStripePaymentIntent",
    //   );
    // }
    /** Resend invitation to a company (provided by companyId) */
    async resendCompanyMembershipInvite({ companyId, userIdentity, }) {
        const response = await this.post(`${this.urls.dashboardV3}/company/${companyId}/member/${encodeURIComponent(userIdentity)}/reminder`);
        return this.assertSchema(response, voidSchema, "resendCompanyMembershipInvite");
    }
    /** Get all the members in a company */
    async getMembersInCompany({ companyId, types, }) {
        const baseUrl = `${this.urls.dashboardV3}/company/${companyId}/member`;
        const query = types?.length
            ? `?${new URLSearchParams({ types: types.join(",") })}`
            : "";
        const url = `${baseUrl}${query}`;
        const response = await this.get(url);
        return this.assertSchema(response, zod.array(companyMemberDetailsSchema), "getMembersInCompany");
    }
    /** Get a specific member of a company */
    async getMemberDetailsInCompany({ companyId, identity, }) {
        const response = await this.get(`${this.urls.dashboardV3}/company/${companyId}/member/${encodeURIComponent(identity)}`);
        return this.assertSchema(response, companyMemberDetailsSchema, "getMemberDetailsInCompany");
    }
    /** Updates a member in a company */
    async updateCompanyMember({ companyId, identity, payload, }) {
        const response = await this.put(`${this.urls.dashboardV3}/company/${companyId}/member/${encodeURIComponent(identity)}`, payload);
        return this.assertSchema(response, companyMemberDetailsSchema, "updateCompanyMember");
    }
    /** Get the context of a company by the company ID */
    async getCompanyContext({ companyId, }) {
        const response = await this.get(`${this.urls.companyV3}/${companyId}/context`);
        return this.assertSchema(response, getCompanyContextSchema, "getCompanyContext");
    }
    /** Get all subscription details of the company */
    async getCompanySubscriptions({ companyId, options, }) {
        const queryString = options
            ? buildQueryParams({
                status: options.status,
                from: options.from,
                until: options.until,
            })
            : "";
        const response = await this.get(`${this.urls.companyV3}/${companyId}/subscriptions${queryString}`);
        return this.assertSchema(response, zod.array(getCompanySubscriptionsSchema), "getCompanySubscriptions");
    }
}

/** All the APIs related to notifications for sending email to sales or support team */
class NotificationClient extends BaseApiClient {
    /** Calling this function would trigger the API and sends an email to appropriate team (requesting a feature or a service) */
    async notificationRequest({ payload, }) {
        const response = await this.post(`${this.urls.notificationV3}/request`, payload);
        return this.assertSchema(response, emptyObjectSchema, "notificationRequest");
    }
}

/** Project access level */
const projectAccessLevelSchema = zod.enum([
    /** When the project is private and only accessible by certain users */
    "private",
    /** When the project is accessible by anyone */
    "unlisted",
]);
/** Reserved project tags for special purposes. "sys:" prefix should be treated as system-reserved */
const systemTagSchema = zod.enum([
    /** tag assigned to the demo project of a company/user */
    "sys:demo",
]);
/** All archiving states for project */
const archivingStateSchema = zod.enum([
    "UNARCHIVED",
    "ARCHIVED",
    "ARCHIVED_READY_TO_DOWNLOAD",
    "ARCHIVED_DOWNLOADED",
]);
/** Unit used to measure a project's area */
const areaUnitSchema = zod.enum(["sqft", "sqm"]);
/**
 * List of all the features that can be assigned to a project and whether they are enabled or not
 * Backend doesn't have a specific type for features and it will return a record of string to boolean
 */
const featureFlagsSchema = zod.record(featureIdentifierSchemas, zod.object({
    enabled: zod.boolean(),
}));
/** Evaluations (e.g. for projects) with an average (calculated by backend) */
const evaluationSchema = zod.object({
    evaluations: zod.array(zod.object({
        /** The ID of the evaluation */
        id: zod.string(),
        /** The value of the evaluation, can be undefined if not set */
        value: zod.number().optional(),
        /** Timestamp of when the evaluation was created */
        createdAt: unixTimestampSchema,
        /** The user who created the evaluation */
        createdBy: zod.string(),
        /** The weight of the evaluation, used for calculating the average */
        weight: zod.number(),
        /** The name of the evaluation, e.g. "Quality", "Safety" */
        name: zod.string(),
    })),
    /** Average for the evaluations, as integer (rounding will be done on backend) */
    average: zod.number(),
});

// TODO: The role is removed and will be added separately. This is because the role is not always available in the group details
/** The properties of a Group */
const groupSchema = zod.object({
    /** The ID of the group */
    id: groupIdSchema,
    /** Human readable name of the group */
    name: zod.string(),
    /** Timestamp of group creation */
    createdAt: unixTimestampSchema,
    /** Number of managers in this groups */
    numManagers: zod.number(),
    /** Number of projects in this groups */
    numProjects: zod.number(),
    /** Square foot assigned by this group */
    sqftAssigned: zod.number(),
    /** Square foot used by this group */
    sqftUsed: zod.number(),
    /** Image of this group */
    imageUrl: zod.string().optional(),
    /** Corresponding thumbnail of this group */
    thumbnailUrl: zod.string().optional(),
});

/** The mutual information of a project. These keys are the same when requesting a project from different routes */
const projectBaseSchema = zod.object({
    /** The access level of the project */
    accessLevel: projectAccessLevelSchema,
    /** The address of the project */
    address: zod.string(),
    /** Timestamp of project creation */
    createdAt: unixTimestampSchema,
    /** Any description for the project. It will only be shown if available */
    description: zod.string().optional(),
    /** The ID of the project */
    id: projectIdSchema,
    /** Timestamp of last project's capture */
    lastCapturedAt: unixTimestampSchema.optional(),
    /** Timestamp of last project download */
    lastDownloadedAt: unixTimestampSchema.optional(),
    /** Timestamp of last project modification */
    modifiedAt: zod.number(),
    /** The name of the project */
    name: zod.string(),
    /** Tags that are assigned to the project */
    tags: zod.array(zod.union([systemTagSchema, zod.string()])),
    /** Link to the project thumbnail */
    thumbnailUrl: zod.string().optional(),
});
/** Project details when searching for one */
const searchedProjectSchema = projectBaseSchema.extend({
    area: zod.object({
        /** Together with the unit attribute, this expresses the area assigned to the project */
        amount: zod.number(),
        /** Projects can either be measured in square feet or square meters */
        unit: areaUnitSchema,
    }),
    /** Project's client name */
    client: zod.string().optional(),
    /** Points to the location where the zip file of the projectContainer is stored */
    fileUrl: zod.string(),
    /** Whether or not the project is standalone */
    standalone: zod.boolean(),
    /** The state of the project in regards to archiving */
    state: archivingStateSchema,
});
/** Project details when requesting from company level */
const companyProjectSchema = projectBaseSchema.extend({
    /** The state of the project in regards to archiving */
    archivingState: archivingStateSchema,
    /** The name of the project's client */
    clientName: zod.string().optional(),
    /** Evaluation data of the project */
    evaluation: evaluationSchema,
    /** List of all the features that can be assigned to a project and whether they are enabled or not */
    features: featureFlagsSchema,
    /** The group in which the project belongs to. Undefined for non-company projects. */
    group: groupSchema.extend({
        role: projectRoleSchema,
    }),
    /** The user and company assigned as managers to the project */
    managers: zod
        .object({
        /** The project manager */
        projectManager: userAsManagerSchema.optional(),
        /** The company manager */
        companyManager: userAsManagerSchema.optional(),
    })
        .optional(),
    /** Permissions of this user on the project */
    permissions: zod.array(projectPermissionSchema),
    /** Role of this user on the project */
    role: projectRoleSchema.optional(),
    /** Area size of the project in sqft */
    sqftUsed: zod.number(),
});
/**
 * All details of a project needed to show in Dashboard
 * This is received from server when requesting project details by ID
 */
const projectDetailsSchema = companyProjectSchema.extend({
    members: zod.array(projectMemberBaseSchema).optional(),
});
/** Part of the context for a project */
const companyProjectContextSchema = zod.object({
    /** Discriminator to differentiate between context-types */
    kind: zod.enum(["COMPANY"]),
    /** The company in which the project belongs to */
    company: companyDetailsSchema.pick({ id: true, name: true, logoUrl: true }),
    /** The company group in which the project is in */
    group: groupSchema.pick({ id: true, name: true }),
    /** The manager of the project */
    projectManager: userAsManagerSchema,
});
/** All the information received for each project when searching for it */
const searchedProjectDetailsSchema = zod.object({
    /** The context of the project */
    context: companyProjectContextSchema,
    /** All the features that are available based on existing plans (whether enabled or not). */
    features: zod.array(featureIdentifierSchemas),
    /** All the features that are available based on existing plans with flags showing whether they are enabled or not */
    featuresAvailable: zod.array(featureSchema),
    /** Permissions of this user on the project */
    permissions: zod.array(projectPermissionSchema),
    /** Information of a searched project */
    project: searchedProjectSchema,
});

/** All the APIs for fetching and updating projects */
class ProjectsClient extends BaseApiClient {
    /** Get the details of a single project by the project ID */
    async getProjectDetails({ companyId, projectId, }) {
        const response = await this.get(`${this.urls.dashboardV3}/company/${companyId}/project/${projectId}`);
        return this.assertSchema(response, projectDetailsSchema, "getProjectDetails");
    }
    /** Create new project by providing the payload */
    async createNewProject({ companyId, payload, }) {
        const response = await this.post(`${this.urls.dashboardV3}/company/${companyId}/project`, payload);
        return this.assertSchema(response, projectDetailsSchema, "createNewProject");
    }
    /** Updates the project details based on the payload */
    async updateProjectDetails({ companyId, projectId, payload, }) {
        const response = await this.post(`${this.urls.dashboardV3}/company/${companyId}/project/${projectId}`, payload);
        return this.assertSchema(response, projectDetailsSchema, "updateProjectDetails");
    }
    /** Get all the statistics for a project for the last 3 years */
    async getProjectStatistics({ companyId, projectId, }) {
        const response = await this.get(`${this.urls.dashboardV3}/company/${companyId}/project/${projectId}/stats`);
        return this.assertSchema(response, zod.array(companyStatisticSchema), "getProjectStatistics");
    }
    /** Sets the Project features for a single project. Works like a PATCH request. Only features in the payload will be modified */
    async setProjectFeatures({ companyId, projectId, payload, }) {
        const response = await this.post(`${this.urls.dashboardV3}/company/${companyId}/project/${projectId}/feature`, payload);
        return this.assertSchema(response, featureFlagsSchema, "setProjectFeatures");
    }
    /** Get the context of a single project by the project ID */
    async getProjectContext({ projectId, }) {
        const response = await this.get(`${this.urls.projectsV3}/${projectId}/context`);
        return this.assertSchema(response, getProjectContextSchema, "getProjectContext");
    }
}

/** All the APIs for fetching and updating users */
class UsersClient extends BaseApiClient {
    /**
     * Gets an object representing all workspaces that the current user belongs to.
     * Can return undefined if the user is not logged in.
     */
    async getCurrentUsersWorkspaces() {
        const response = await this.get(`${this.urls.usersV3}/me/workspaces`);
        return this.assertSchema(response, getCurrentUsersWorkspacesSchema, "getCurrentUsersWorkspaces");
    }
    /** Get profile information for the current user */
    async getCurrentUserProfileInfos(companyId) {
        const response = await this.get(`${this.urls.dashboardV3}/company/${companyId}/me`);
        return this.assertSchema(response, companyMemberBaseSchema, "getCurrentUserProfileInfos");
    }
    /** Gets both the hash and the user id to identify users in Intercom. */
    async getIntercomHash() {
        const response = await this.get(`${this.urls.usersV3}/intercom`);
        return this.assertSchema(response, intercomDataSchema, "getIntercomHash");
    }
}

/** Client for interacting with the Core API. */
class CoreApiClient {
    users;
    company;
    projects;
    notification;
    constructor({ baseUrl, headers, onLogAssertion }) {
        this.users = new UsersClient({ baseUrl, headers, onLogAssertion });
        this.company = new CompanyClient({ baseUrl, headers, onLogAssertion });
        this.projects = new ProjectsClient({ baseUrl, headers, onLogAssertion });
        this.notification = new NotificationClient({
            baseUrl,
            headers,
            onLogAssertion,
        });
    }
}

var index$2 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

/** Determines the notification type */
const notificationTypeSchema = zod.enum([
    "feature_request",
    "BETA_REQUEST",
    "service_request",
    "expansion_request",
    "feedback",
]);

var index$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ConstraintSchema: ConstraintSchema,
    archivingStateSchema: archivingStateSchema,
    areaUnitSchema: areaUnitSchema,
    axeSchema: axeSchema,
    brandingSettingsSchema: brandingSettingsSchema,
    bundleIdentifierSchemas: bundleIdentifierSchemas,
    chartDataGenerationSchema: chartDataGenerationSchema,
    companyCommunicationSettingsSchema: companyCommunicationSettingsSchema,
    companyContextSchema: companyContextSchema,
    companyDetailsSchema: companyDetailsSchema,
    companyIdSchema: companyIdSchema,
    companyMemberBaseSchema: companyMemberBaseSchema,
    companyMemberDetailsSchema: companyMemberDetailsSchema,
    companyProjectContextSchema: companyProjectContextSchema,
    companyProjectSchema: companyProjectSchema,
    companyRoleSchema: companyRoleSchema,
    companyStatisticSchema: companyStatisticSchema,
    companySubscriptionOptionsSchema: companySubscriptionOptionsSchema,
    companyWithRoleDetailsSchema: companyWithRoleDetailsSchema,
    constraintIdSchema: constraintIdSchema,
    constraintTypeSchema: constraintTypeSchema,
    createConstraintPayloadSchema: createConstraintPayloadSchema,
    createGroupPermissionSchema: createGroupPermissionSchema,
    createStripePaymentIntentSchema: createStripePaymentIntentSchema,
    emptyObjectSchema: emptyObjectSchema,
    evaluationSchema: evaluationSchema,
    featureFlagsSchema: featureFlagsSchema,
    featureIdentifierSchemas: featureIdentifierSchemas,
    featureSchema: featureSchema,
    getCompanyContextSchema: getCompanyContextSchema,
    getCompanySubscriptionsSchema: getCompanySubscriptionsSchema,
    getCurrentUsersWorkspacesSchema: getCurrentUsersWorkspacesSchema,
    getProjectContextSchema: getProjectContextSchema,
    groupIdSchema: groupIdSchema,
    groupMemberSchema: groupMemberSchema,
    groupSchema: groupSchema,
    intercomDataSchema: intercomDataSchema,
    internalChartTypeSchema: internalChartTypeSchema,
    iso8601DateSchema: iso8601DateSchema,
    kindSchema: kindSchema,
    memberSubjectTypeSchema: memberSubjectTypeSchema,
    notificationTypeSchema: notificationTypeSchema,
    projectAccessLevelSchema: projectAccessLevelSchema,
    projectBaseSchema: projectBaseSchema,
    projectDetailsSchema: projectDetailsSchema,
    projectIdSchema: projectIdSchema,
    projectMemberBaseSchema: projectMemberBaseSchema,
    projectPermissionSchema: projectPermissionSchema,
    projectRoleSchema: projectRoleSchema,
    quotaIdentifierSchema: quotaIdentifierSchema,
    quotaSchema: quotaSchema,
    searchedProjectDetailsSchema: searchedProjectDetailsSchema,
    searchedProjectSchema: searchedProjectSchema,
    serverChartTypeSchema: serverChartTypeSchema,
    setMemberPermissionsInCompanySchema: setMemberPermissionsInCompanySchema,
    statisticNameSchema: statisticNameSchema,
    stripeProductSchema: stripeProductSchema,
    subscriptionStatusSchema: subscriptionStatusSchema,
    systemTagSchema: systemTagSchema,
    unixTimestampSchema: unixTimestampSchema,
    updateConstraintPayloadSchema: updateConstraintPayloadSchema,
    userAsManagerSchema: userAsManagerSchema,
    userIdSchema: userIdSchema,
    userPermissionNameSchema: userPermissionNameSchema,
    userPermissionSchema: userPermissionSchema,
    userSchema: userSchema,
    voidSchema: voidSchema,
    workspaceRoleSchema: workspaceRoleSchema,
    workspaceSchema: workspaceSchema,
    workspaceTypeSchema: workspaceTypeSchema
});

/** Maps backend role strings for workspace roles making sure all possible roles are reflected */
const workspaceRole = {
    /* eslint-disable @typescript-eslint/naming-convention -- keys are coming from backend */
    Administrator: "Administrator",
    owner: "owner",
    "Enterprise Admin": "Enterprise Admin",
    "Enterprise Viewer": "Enterprise Viewer",
    "Company Admin": "Company Admin",
    "Group Manager": "Group Manager",
    "Project Manager": "Project Manager",
    "Company Member": "Company Member",
    /* eslint-enable */
};
/** All the workspace roles */
const allWorkspaceRoles = Object.values(workspaceRole);
/** Maps backend role strings for company roles making sure all possible roles are reflected */
const companyRole = {
    /* eslint-disable @typescript-eslint/naming-convention -- keys are coming from backend */
    COMPANY_EXECUTIVE: "COMPANY_EXECUTIVE",
    COMPANY_VIEWER: "COMPANY_VIEWER",
    COMPANY_MANAGER: "COMPANY_MANAGER",
    GROUP_VIEWER: "GROUP_VIEWER",
    PROJECT_MANAGER: "PROJECT_MANAGER",
    MEMBER: "MEMBER",
    /* eslint-enable */
};
/** All the company roles */
const allCompanyRoles = Object.values(companyRole);
/** Maps backend role strings for project roles making sure all possible roles are reflected */
const projectRole = {
    /* eslint-disable @typescript-eslint/naming-convention -- keys are coming from backend */
    RESOURCE_OWNER: "RESOURCE_OWNER",
    RESOURCE_ADMIN: "RESOURCE_ADMIN",
    RESOURCE_EDITOR: "RESOURCE_EDITOR",
    RESOURCE_VIEWER: "RESOURCE_VIEWER",
    /* eslint-enable */
};
/** All the project roles */
const allProjectRoles = Object.values(projectRole);
/** Maps backend role strings for roles that can be assigned to group making sure all possible roles are reflected */
const groupRole = {
    /* eslint-disable @typescript-eslint/naming-convention -- keys are coming from backend */
    COMPANY_MANAGER: "COMPANY_MANAGER",
    GROUP_VIEWER: "GROUP_VIEWER",
    PROJECT_MANAGER: "PROJECT_MANAGER",
    /* eslint-enable */
};
/** All the group roles */
const allGroupRoles = Object.values(groupRole);

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    allCompanyRoles: allCompanyRoles,
    allGroupRoles: allGroupRoles,
    allProjectRoles: allProjectRoles,
    allWorkspaceRoles: allWorkspaceRoles,
    companyRole: companyRole,
    groupRole: groupRole,
    projectRole: projectRole,
    workspaceRole: workspaceRole
});

export { CoreApiClient, index as FaroCoreApiConst, index$1 as FaroCoreApiSchemas, index$2 as FaroCoreApiTypes };
